/**
 * Guardians v2 API client — ``/api/v1/guardiansv2``.
 */

import { apiRequest } from "../client";
import {
  TIME_RANGE_TO_API,
  type Audit,
  type AuditType,
  type GuardianPromptV2,
  type GuardianTicketV2,
  type GuardianType,
  type GuardiansTimeRange,
  type GuardiansV2Settings,
  type RatingLevel,
  type TicketFolderV2,
  type TicketStatus,
  type ActionItemType,
  type Alert,
  type AlertAudience,
  type AlertCategory,
  type AlertSource,
  type AlertStatus,
  type AuditableEntityKind,
  type AuditableEntityRow,
  type AuditQueueRef,
  type TaskAuditTargetsResponse,
  type GuardianRule,
  type IgnoreRule,
  type GuardiansUserPreferences,
  type GuardiansUserPreferencesPatch,
  type ScreeningDocument,
} from "./types";

export type {
  Alert,
  Audit,
  AuditReport,
  GuardianTicketV2,
  GuardiansV2Settings,
} from "./types";
export {
  TIME_RANGE_TO_API,
  ALERT_SOURCE_LABELS,
  ALERT_CATEGORY_LABELS,
  ALERT_AUDIENCE_LABELS,
  AUDIT_TYPE_LABELS,
  PERSONA_LABELS,
  TICKET_TYPE_LABELS,
  AUDITABLE_ENTITY_KINDS,
  AUDITABLE_ENTITY_KIND_ORDER,
  NEW_AUDIT_SEARCH_KINDS,
  ENTITY_KIND_LABELS,
  alertSourceBadgeColor,
  alertBadgeColor,
  alertBadgeLabel,
  guardianCheckFocusRef,
  isAuditType,
  v2ScopeToAuditScope,
  auditableEntityKey,
  auditableEntityToQueueRef,
} from "./types";
export type {
  AuditableEntityKind,
  AuditableEntityRow,
  AuditQueueRef,
  TaskAuditTargetsResponse,
} from "./types";
export type { V2AuditScopePayload } from "./types";
export type {
  StatsTimePeriod,
  OverviewStats,
  CatalogueStat,
  IssueStat,
  TrendResponse,
  AlertStats,
  StatsFilterParams,
} from "./types";
export {
  getStatsOverview,
  getStatsByCatalogue,
  getStatsIssues,
  getStatsTrend,
  getStatsAlerts,
} from "./stats";

const BASE = "/guardiansv2";

export interface ListAuditsParams {
  client_id?: string;
  audit_type?: AuditType;
  rating?: RatingLevel;
  mission_id?: string;
  template_id?: string;
  task_id?: string;
  interaction_id?: string;
  agent_id?: string;
  catalogue_id?: string;
  time_range?: GuardiansTimeRange;
  skip?: number;
  limit?: number;
}

function timePeriodFromRange(time_range?: GuardiansTimeRange): "1d" | "1w" | "1m" | "all" | undefined {
  if (!time_range) return undefined;
  return TIME_RANGE_TO_API[time_range];
}

export async function listAudits(
  params: ListAuditsParams = {}
): Promise<{ audits: Audit[]; total: number; skip: number; limit: number }> {
  const { time_range, ...rest } = params;
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits`,
    params: {
      ...rest,
      time_period: timePeriodFromRange(time_range),
    },
  });
}

export async function getAudit(auditId: string): Promise<Audit> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits/${encodeURIComponent(auditId)}`,
  });
}

export async function listAuditsByMission(
  missionId: string,
  params: { client_id?: string; skip?: number; limit?: number } = {}
): Promise<{ audits: Audit[]; total: number; skip: number; limit: number }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits/by-mission/${encodeURIComponent(missionId)}`,
    params,
  });
}

export async function listAuditsByTask(
  taskId: string,
  params: { client_id?: string; skip?: number; limit?: number } = {}
): Promise<{ audits: Audit[]; total: number; skip: number; limit: number }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits/by-task/${encodeURIComponent(taskId)}`,
    params,
  });
}

export async function listAuditsByInteraction(
  interactionId: string,
  params: { client_id?: string; skip?: number; limit?: number } = {}
): Promise<{ audits: Audit[]; total: number; skip: number; limit: number }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits/by-interaction/${encodeURIComponent(interactionId)}`,
    params,
  });
}

export async function listAuditsByArtifact(
  artifactId: string,
  params: { client_id?: string; skip?: number; limit?: number } = {}
): Promise<{ audits: Audit[]; total: number; skip: number; limit: number }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits/by-artifact/${encodeURIComponent(artifactId)}`,
    params,
  });
}

export interface ListAlertsParams {
  client_id?: string;
  source?: AlertSource;
  status?: AlertStatus;
  mission_id?: string;
  template_id?: string;
  task_id?: string;
  interaction_id?: string;
  audit_id?: string;
  agent_id?: string;
  catalogue_id?: string;
  category?: AlertCategory;
  audience?: AlertAudience;
  time_range?: GuardiansTimeRange;
  skip?: number;
  limit?: number;
}

export async function listAlerts(
  params: ListAlertsParams = {}
): Promise<{ alerts: Alert[]; total: number; skip: number; limit: number }> {
  const { time_range, ...rest } = params;
  return apiRequest({
    method: "GET",
    url: `${BASE}/alerts`,
    params: {
      ...rest,
      time_period: timePeriodFromRange(time_range),
    },
  });
}

export async function getAlert(alertId: string): Promise<Alert> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/alerts/${encodeURIComponent(alertId)}`,
  });
}

export async function ignoreAlert(
  alertId: string,
  body?: { signal_type?: string | null }
): Promise<{ rule: IgnoreRule; catalogue_key: string; signal_type: string; alert_id: string }> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/alerts/${encodeURIComponent(alertId)}/ignore`,
    data: body ?? {},
  });
}

export async function archiveAlert(alertId: string): Promise<Alert> {
  return apiRequest({
    method: "PATCH",
    url: `${BASE}/alerts/${encodeURIComponent(alertId)}`,
    data: { status: "archived" },
  });
}

export interface AuditInvestigationOptions {
  user_focus?: string | null;
}

/** Queue a context-rich audit anchored on an alert's subject. */
export async function auditFromAlert(
  alertId: string,
  options?: AuditInvestigationOptions
): Promise<{ status: string; task_id: string; audit_id: string }> {
  const focus = options?.user_focus?.trim();
  return apiRequest({
    method: "POST",
    url: `${BASE}/alerts/${encodeURIComponent(alertId)}/audit`,
    data: focus ? { user_focus: focus } : {},
  });
}

/** Queue an investigation audit from any screening (pass or fail). */
export async function auditFromScreening(
  screeningId: string,
  options?: AuditInvestigationOptions
): Promise<{ status: string; task_id: string; audit_id: string }> {
  const focus = options?.user_focus?.trim();
  return apiRequest({
    method: "POST",
    url: `${BASE}/screenings/${encodeURIComponent(screeningId)}/audit`,
    data: focus ? { user_focus: focus } : {},
  });
}

export interface AuditLaunchRef {
  task_id: string;
  audit_id: string;
}

export async function runMissionAudit(
  missionId: string
): Promise<{ queued: number; task_ids: string[]; launches?: AuditLaunchRef[] }> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/audits/by-mission/${encodeURIComponent(missionId)}/run-all`,
  });
}

export async function runTaskAudit(
  taskId: string
): Promise<{ queued: number; task_ids: string[]; launches?: AuditLaunchRef[] }> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/audits/by-task/${encodeURIComponent(taskId)}/run-all`,
  });
}

export async function rerunAudit(
  auditId: string,
  personas?: GuardianType[] | null
): Promise<{ status: string; task_id: string; audit_id: string }> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/audits/rerun/${encodeURIComponent(auditId)}`,
    data: personas !== undefined && personas !== null ? { personas } : undefined,
  });
}

export async function getTaskAuditTargets(
  taskId: string
): Promise<TaskAuditTargetsResponse> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits/targets/by-task/${encodeURIComponent(taskId)}`,
  });
}

export interface SearchAuditableEntitiesParams {
  q: string;
  kinds?: AuditableEntityKind[];
  limit?: number;
  client_id?: string;
}

/** FastAPI expects repeated keys (`kinds=a&kinds=b`), not `kinds[]`. */
const FASTAPI_LIST_PARAMS_SERIALIZER = { indexes: null } as const;

export async function searchAuditableEntities(
  params: SearchAuditableEntitiesParams
): Promise<{ entities: AuditableEntityRow[] }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits/search-entities`,
    params: {
      q: params.q,
      kinds: params.kinds,
      limit: params.limit,
      client_id: params.client_id,
    },
    paramsSerializer: FASTAPI_LIST_PARAMS_SERIALIZER,
  });
}

export interface ResolveAuditableEntityParams {
  id: string;
  kinds?: AuditableEntityKind[];
}

export async function resolveAuditableEntity(
  params: ResolveAuditableEntityParams
): Promise<{ entities: AuditableEntityRow[] }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/audits/entities/resolve`,
    params: {
      id: params.id,
      kinds: params.kinds,
    },
    paramsSerializer: FASTAPI_LIST_PARAMS_SERIALIZER,
  });
}

export async function queueAudits(
  targets: AuditQueueRef[]
): Promise<{
  queued: number;
  task_ids?: string[];
  launches?: AuditLaunchRef[];
  failed: Array<{ ref: AuditQueueRef; reason: string }>;
}> {
  return apiRequest({
    method: "POST",
    url: `${BASE}/audits/queue`,
    data: { targets },
  });
}

export interface ListTicketsParams {
  client_id?: string;
  status?: TicketStatus;
  type?: ActionItemType;
  severity?: RatingLevel;
  mission_id?: string;
  task_id?: string;
  interaction_id?: string;
  include_duplicates?: boolean;
  time_range?: GuardiansTimeRange;
  skip?: number;
  limit?: number;
}

export async function listTickets(
  params: ListTicketsParams = {}
): Promise<{ tickets: GuardianTicketV2[]; total: number; skip: number; limit: number }> {
  const { time_range, ...rest } = params;
  return apiRequest({
    method: "GET",
    url: `${BASE}/tickets`,
    params: {
      ...rest,
      time_period: timePeriodFromRange(time_range),
    },
  });
}

export async function getTicket(ticketRef: string): Promise<GuardianTicketV2> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/tickets/${encodeURIComponent(ticketRef)}`,
  });
}

export interface TicketWorkflowPatch {
  status?: TicketStatus;
  assigned_to?: string | null;
  priority?: number;
  folder_id?: string | null;
}

export async function patchTicketWorkflow(
  ticketRef: string,
  body: TicketWorkflowPatch
): Promise<GuardianTicketV2> {
  return apiRequest({
    method: "PATCH",
    url: `${BASE}/tickets/${encodeURIComponent(ticketRef)}/workflow`,
    data: body,
  });
}

export async function getSettings(): Promise<GuardiansV2Settings> {
  return apiRequest({ method: "GET", url: `${BASE}/settings` });
}

export async function patchSettings(
  body: Partial<GuardiansV2Settings>
): Promise<GuardiansV2Settings> {
  return apiRequest({ method: "PATCH", url: `${BASE}/settings`, data: body });
}

export async function rerunDedup(): Promise<{ task_id: string }> {
  return apiRequest({ method: "POST", url: `${BASE}/rerun-dedup` });
}

export async function getScreeningPromptPreview(
  catalogueKey: string,
  clientId?: string | null,
): Promise<{ catalogue_key: string; client_id?: string; context: Record<string, string> }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/prompts/screening/preview`,
    params: { catalogue_key: catalogueKey, ...(clientId ? { client_id: clientId } : {}) },
  });
}

export async function listPrompts(audit_type?: string): Promise<{ prompts: GuardianPromptV2[] }> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/prompts`,
    params: audit_type ? { audit_type } : undefined,
  });
}

export async function getPrompt(
  auditType: string,
  persona: string
): Promise<GuardianPromptV2> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/prompts/${encodeURIComponent(auditType)}/${encodeURIComponent(persona)}`,
  });
}

export async function updatePrompt(
  auditType: string,
  persona: string,
  body: {
    system_prompt: string;
    user_prompt_template: string;
    description?: string;
  }
): Promise<GuardianPromptV2> {
  return apiRequest({
    method: "PUT",
    url: `${BASE}/prompts/${encodeURIComponent(auditType)}/${encodeURIComponent(persona)}`,
    data: body,
  });
}

export async function listFolders(): Promise<{ folders: TicketFolderV2[] }> {
  return apiRequest({ method: "GET", url: `${BASE}/folders` });
}

export async function createFolder(body: {
  name: string;
  color?: string | null;
  default_for_catalogue_id?: string | null;
}): Promise<TicketFolderV2> {
  return apiRequest({ method: "POST", url: `${BASE}/folders`, data: body });
}

export async function updateFolder(
  folderId: string,
  body: Partial<{
    name: string;
    color: string | null;
    archived: boolean;
    default_for_catalogue_id: string | null;
  }>
): Promise<TicketFolderV2> {
  return apiRequest({
    method: "PATCH",
    url: `${BASE}/folders/${encodeURIComponent(folderId)}`,
    data: body,
  });
}

export async function listRulesCatalogue(): Promise<import("./types").RulesCatalogueResponse> {
  return apiRequest({ method: "GET", url: `${BASE}/rules/catalogue` });
}

export async function getRules(
  catalogueKey: string,
  clientId?: string | null,
): Promise<import("./types").RuleSetResponse> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/rules/${encodeURIComponent(catalogueKey)}`,
    params: clientId ? { client_id: clientId } : undefined,
  });
}

export async function getRuleStats(
  catalogueKey: string,
  limit = 100,
  clientId?: string | null,
): Promise<import("./types").RuleStatsResponse> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/rules/${encodeURIComponent(catalogueKey)}/stats`,
    params: { limit, ...(clientId ? { client_id: clientId } : {}) },
  });
}

export async function putRules(
  catalogueKey: string,
  body: {
    rules: import("./types").GuardianRule[];
    catalogue_display_name?: string | null;
  },
  clientId?: string | null,
): Promise<import("./types").RuleSetResponse> {
  return apiRequest({
    method: "PUT",
    url: `${BASE}/rules/${encodeURIComponent(catalogueKey)}`,
    params: clientId ? { client_id: clientId } : undefined,
    data: body,
  });
}

export async function assistRules(body: {
  prompt: string;
  catalogue_key?: string | null;
  client_id?: string | null;
}): Promise<{ rules: GuardianRule[] }> {
  return apiRequest({ method: "POST", url: `${BASE}/rules/assist`, data: body });
}

export async function getScreeningByAlert(alertId: string): Promise<ScreeningDocument> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/screenings`,
    params: { alert_id: alertId },
  });
}

export async function getScreening(
  screeningId: string,
  options?: { include_workload?: boolean },
): Promise<ScreeningDocument> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/screenings/${encodeURIComponent(screeningId)}`,
    params: options?.include_workload === false ? { include_workload: false } : undefined,
  });
}

export async function listScreenings(params: {
  mission_id?: string;
  template_id?: string;
  task_id?: string;
  catalogue_key?: string;
  agent_id?: string;
  catalogue_id?: string;
  raised_alert?: boolean;
  subject_type?: "task" | "mission";
  time_period?: "1d" | "1w" | "1m" | "all";
  skip?: number;
  limit?: number;
}): Promise<import("./types").ScreeningListResponse> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/screenings`,
    params,
  });
}

export async function getMissionConversation(missionId: string): Promise<import("./types").MissionConversation> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/missions/${encodeURIComponent(missionId)}/conversation`,
  });
}

export async function getTaskConversation(taskId: string): Promise<import("./types").MissionConversation> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/tasks/${encodeURIComponent(taskId)}/conversation`,
  });
}

export async function getUserPreferences(): Promise<GuardiansUserPreferences> {
  return apiRequest({
    method: "GET",
    url: `${BASE}/user-preferences`,
  });
}

export async function patchUserPreferences(
  patch: GuardiansUserPreferencesPatch,
): Promise<GuardiansUserPreferences> {
  return apiRequest({
    method: "PATCH",
    url: `${BASE}/user-preferences`,
    data: patch,
  });
}
